import type { MakeTransferBody } from 'api/v2/transactions/makeTransfer';
import makeTransfer from 'api/v2/transactions/makeTransfer';
import useMutation from 'hooks/react-query/useMutation';

import type { Transaction } from 'types/transactions.types';
import { appendQP } from 'utils/helpers';

const useMakeTransfer = ({
  withRedirection,
  onTransferSuccess,
  redirectionParams,
  redirectionPath,
  successRedirectUri: successRedirectUriOverride,
}: {
  withRedirection?: boolean;
  onTransferSuccess?: (transaction?: Transaction) => void;
  redirectionParams?: Record<string, any>;
  redirectionPath?: string;
  successRedirectUri?: string;
}) => {
  return useMutation(
    (
      body: Omit<MakeTransferBody, 'successRedirectUri' | 'errorRedirectUri'>,
    ) => {
      // Determine if this is a scheduled payment by checking the body
      // - requestedExecutionAt: scheduled transfer (OneTime + future)
      // - period: standing order (Daily, Weekly, Monthly)
      const isScheduledPayment = !!(body.requestedExecutionAt || body.period);

      const redirectQueryParams = {
        successfulPayment: 'true',
        amount: body.amount?.value,
        currency: body.amount?.currency,
        ...(isScheduledPayment && { isScheduledPayment: 'true' }),
        ...redirectionParams,
      };

      let successRedirectUri: string;
      if (successRedirectUriOverride) {
        const parsed = new URL(successRedirectUriOverride);
        const mergedSearch = new URLSearchParams(window.location.search);
        new URLSearchParams(parsed.search).forEach((value, key) => {
          mergedSearch.set(key, value);
        });
        const queryString = appendQP(
          mergedSearch.toString(),
          redirectQueryParams,
        );
        successRedirectUri = `${parsed.origin}${parsed.pathname}?${queryString}`;
      } else {
        const base = `${window.location.origin}${
          redirectionPath || window.location.pathname
        }`;
        const queryString = appendQP(
          window.location.search,
          redirectQueryParams,
        );
        successRedirectUri = `${base}?${queryString}`;
      }

      return makeTransfer({
        ...body,
        successRedirectUri,
        errorRedirectUri: `${window.location.origin}${window.location.pathname}?error=true`,
      });
    },
    {
      onSuccess: ({ redirectUri }) => {
        onTransferSuccess?.();

        if (withRedirection) {
          window.location.href = redirectUri;
        }
      },
    },
  );
};

export default useMakeTransfer;
